import { bearerFetcher, useAuthBearerFetch } from "matsuri-hooks"
import { useAuth } from "m2m-components/storage/useM2mAuth"
import { m2mCleaning_cleanerV4 } from "@/endpoints/m2m-cleaning.cleaner-v4"
import {
    EXPENSE_DATE_OUT_OF_RANGE_MESSAGE,
    INVALID_AMOUNT_MESSAGE,
    INVALID_ROUTE_DESCRIPTION_MESSAGE,
    TransportationExpense
} from "@/model/transportationExpense"
import { DateString } from "@/utils/dateString"

interface ListTransportationExpensesOutput {
    transportationExpenses: TransportationExpense[]
}

// 履歴として表示する前月1日〜当日は最大62日（31日の月が2つ続く場合）で、
// 交通費は1日1件までしか申請できないため62件あれば足りる
export const TRANSPORTATION_EXPENSE_HISTORY_LIMIT = 62

interface ListTransportationExpensesParams {
    limit?: number
    /** この日を含む、これ以降の申請を取得する */
    expenseDateFrom?: DateString
    /** この日を含む、これ以前の申請を取得する */
    expenseDateTo?: DateString
}

/**
 * レスポンスは expense_date 降順（同日内は created_at 降順）で返る。
 * 期間を指定した場合もこの並び順は変わらない。
 */
export const useTransportationExpensesForCleaner = ({
    limit = TRANSPORTATION_EXPENSE_HISTORY_LIMIT,
    expenseDateFrom,
    expenseDateTo
}: ListTransportationExpensesParams = {}) => {
    const { token } = useAuth()
    const endpoint = m2mCleaning_cleanerV4.listTransportationExpensesByCleaner

    return useAuthBearerFetch<ListTransportationExpensesOutput>(
        token,
        endpoint(),
        {
            method: endpoint.method,
            body: JSON.stringify({ limit, expenseDateFrom, expenseDateTo })
        }
    )
}

export type TransportationExpenseErrorType =
    | "expense_date_out_of_range"
    | "invalid_amount"
    | "invalid_route_description"
    | "no_assigned_cleaning"
    | "transportation_expense_already_registered"
    | "transportation_expense_not_found"
    | "transportation_expense_not_editable"

// キーはm2m-cleaning側 TransportationExpenseError の error_type() と一致させること
export const transportationExpenseErrorMessages: Record<
    TransportationExpenseErrorType,
    string
> = {
    expense_date_out_of_range: EXPENSE_DATE_OUT_OF_RANGE_MESSAGE,
    invalid_amount: INVALID_AMOUNT_MESSAGE,
    invalid_route_description: INVALID_ROUTE_DESCRIPTION_MESSAGE,
    no_assigned_cleaning: "この日にアサインされた清掃がないため申請できません",
    transportation_expense_already_registered: "この日の分はすでに申請済みです",
    transportation_expense_not_found: "対象の申請が見つかりません",
    transportation_expense_not_editable: "修正できるのは清掃日の翌日までです"
}

interface TransportationExpenseErrorResponse {
    errorType: TransportationExpenseErrorType
    error: string
}

interface RegisterTransportationExpenseInput {
    expenseDate: DateString
    amount: number
    routeDescription: string
}

interface RegisterTransportationExpenseOutput {
    transportationExpense: TransportationExpense
}

interface UpdateTransportationExpenseInput {
    transportationExpenseId: string
    amount: number
    routeDescription: string
}

// 更新APIは成功時に空オブジェクトを返す
type UpdateTransportationExpenseOutput = Record<string, never>

export const useTransportationExpenseMutation = () => {
    const { token } = useAuth()
    const registerEndpoint = m2mCleaning_cleanerV4.registerTransportationExpense
    const updateEndpoint = m2mCleaning_cleanerV4.updateTransportationExpense

    return {
        registerTransportationExpense: (
            input: RegisterTransportationExpenseInput
        ) =>
            bearerFetcher<
                RegisterTransportationExpenseOutput,
                TransportationExpenseErrorResponse
            >(token, registerEndpoint(), {
                method: registerEndpoint.method,
                body: JSON.stringify(input)
            }),
        updateTransportationExpense: (
            input: UpdateTransportationExpenseInput
        ) =>
            bearerFetcher<
                UpdateTransportationExpenseOutput,
                TransportationExpenseErrorResponse
            >(token, updateEndpoint(), {
                method: updateEndpoint.method,
                body: JSON.stringify(input)
            })
    }
}
